import { useEffect } from "react";
import IconSayAboutUs from "../../../../components/ui/icon-sayaboutus";
import { useFeedbackFlow } from "../../hooks/use-feedback-flow";
import type { ApiPage, Brandkit, FormBrand } from "../../types/api.types";

interface StepPositiveThankYouProps {
	brandkit: Brandkit;
	brand: FormBrand;
	showBackButton?: boolean;
	onBack?: () => void;
	stepKey?: string;
	pageConfig: ApiPage;
	isDarkMode?: boolean;
}

export default function StepPositiveThankYou({
	brandkit,
	brand,
	stepKey,
	pageConfig,
	isDarkMode,
}: StepPositiveThankYouProps) {
	const { complete } = useFeedbackFlow();

	useEffect(() => {
		complete();
	}, [complete]);

	const title = pageConfig.attributes.title || "You're amazing!";
	const description =
		pageConfig.attributes.description ||
		"Your testimonial has been submitted successfully. We truly appreciate you taking the time to share your experience with us.";
	const imageUrl =
		"image_url" in pageConfig.attributes && pageConfig.attributes.image_url
			? pageConfig.attributes.image_url
			: "/images/thankyou-illustration-3.jpg";
	const showBranding = brand?.branding !== "disabled";

	const brandLogo =
		brandkit?.logo_icon_with_label_url ||
		brandkit?.logo_icon_only_url ||
		brandkit?.logo_label_only_url ||
		brandkit?.logo_inverted_icon_with_label_url ||
		brandkit?.logo_inverted_icon_only_url ||
		brandkit?.logo_inverted_label_only_url ||
		null;

	return (
		<div
			className={`min-h-screen flex flex-col items-center justify-center px-6 relative overflow-hidden ${isDarkMode ? "bg-gray-950" : "bg-white"}`}
		>
			{/* Logo */}
			<div
				key={stepKey ? `logo-${stepKey}` : undefined}
				className="animate-fade-in-up mb-6"
			>
				{brandLogo ? (
					<img
						src={brandLogo}
						alt={brand.name}
						className="h-10 md:h-12 w-auto object-contain"
					/>
				) : (
					<span
						className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-t-primary"}`}
						style={{
							fontFamily: "var(--font-body)",
							fontWeight: "var(--font-body-weight)",
						}}
					>
						{brand.name}
					</span>
				)}
			</div>

			{/* Title */}
			<div
				key={stepKey ? `title-${stepKey}` : undefined}
				className="animate-fade-in-up animation-delay-100 text-center"
			>
				<h1
					className={`text-[36px] font-semibold leading-tight tracking-tight ${isDarkMode ? "text-white" : "text-t-primary"}`}
					style={{
						fontFamily: "var(--heading-body)",
						fontWeight: "var(--font-heading-weight)",
					}}
				>
					{title}
				</h1>
			</div>

			{/* Subtitle */}
			<div
				key={stepKey ? `subtitle-${stepKey}` : undefined}
				className="animate-fade-in-up animation-delay-200 text-center mt-3 max-w-md"
			>
				<p
					className={`text-base md:text-lg leading-relaxed ${isDarkMode ? "text-gray-300" : "text-secondary"}`}
					style={{
						fontFamily: "var(--font-body)",
						fontWeight: "var(--font-body-weight)",
					}}
				>
					{description}
				</p>
			</div>

			<div className="w-full max-w-md mx-auto group cursor-pointer mt-10">
				<img src={imageUrl} alt="" className="w-full h-auto" />
			</div>

			{/* Free plan panel */}
			{showBranding && (
				<div className="mt-8 p-6 text-gray-900 bg-white rounded-lg shadow-lg border border-gray-100 max-w-md w-full text-center">
					<IconSayAboutUs className="w-12 h-12 mx-auto mb-4" />
					<h3 className="text-lg font-semibold text-gray-900 mb-2">
						Like this testimonial experience?
					</h3>
					<p className="text-sm text-gray-600 mb-4 max-w-[256px] mx-auto">
						Start collecting testimonials for your business with{" "}
						<strong>Say About Us</strong>.
					</p>
					<a
						href="https://sayabout.us/"
						target="_blank"
						rel="noopener noreferrer"
						className="inline-block px-6 py-2 bg-[#222] text-white font-medium rounded-lg hover:bg-[#333] transition-all duration-200 text-sm"
					>
						Get Started
					</a>
				</div>
			)}
		</div>
	);
}
